import { chanceRulesDefaults } from '../s2/defaults';
import type { ChanceEventDef, ChanceEventsDef } from '../s2/types';
import type { Err, Ids } from './common';
import { filled, idsOf, rangeMsg, sectionList } from './s2common';
import { MAX_SQUAD_SIZE, type Bundle } from './types';

/** `ContentValidator.ChanceModes` (RaidModes Team, InPerson, Trigger, Legacy). */
export const CHANCE_MODES = ['team', 'in_person', 'trigger', 'legacy'] as const;
const TRIGGER_KINDS = ['enter_room', 'first_runner_seen', 'pass_set_piece'];

/**
 * Port of shared-cs `ContentValidator.Chance.cs` (`ChanceSections`: chanceEvents), S2-CR. Same errors, same wording, same order. The
 * chain itself (cells, the timeout option, depth, the tone rule) is the engine's validator (`beats.ts`).
 */
export function validateChance(b: Bundle, err: Err, warn: Err, ids: Ids): void {
  void warn;
  void ids;
  const S = 'chanceEvents';
  const section: ChanceEventsDef = b.chanceEvents ?? {};
  const r = filled(chanceRulesDefaults(), section.rules ?? undefined);
  const rule = (path: string, m: string) => err(S, undefined, `rules.${path}`, m);
  if (section.rules === null) err(S, undefined, 'rules', "chanceEvents needs 'rules'.");
  const cp = rangeMsg(r.chancePerRaid, 0, 1, 'chanceEvents.rules', 'chancePerRaid');
  if (cp) rule('chancePerRaid', cp);
  if (r.maxPerRaid < 0 || r.maxPerRaid > 1)
    rule(
      'maxPerRaid',
      'chanceEvents.rules needs maxPerRaid in 0..1 (one event per raid; a chain counts as one).',
    );
  if (r.maxPerRunDay < 0 || r.maxPerRunDay > 10)
    rule('maxPerRunDay', 'chanceEvents.rules needs maxPerRunDay in 0..10.');
  if (r.minRaidsBetween < 0 || r.minRaidsBetween > 20)
    rule('minRaidsBetween', 'chanceEvents.rules needs minRaidsBetween in 0..20.');
  if (r.skipFirstRaids < 0 || r.skipFirstRaids > 20)
    rule('skipFirstRaids', 'chanceEvents.rules needs skipFirstRaids in 0..20.');

  const events = sectionList<ChanceEventDef>(
    section.events,
    'chanceEvents.events',
    err,
    S,
    'events',
  );
  idsOf(events, (e) => e.id, 'chance event', err, S, 'events');
  const chainsUsed = new Set<string>();
  const setPieces = new Set((b.setPieces ?? []).filter((sp) => sp?.id).map((sp) => sp!.id!));
  const categories = b.categories ?? [];

  for (const [e, i] of events) {
    const at = (p: string, m: string) => err(S, undefined, `events.${i}${p ? `.${p}` : ''}`, m);
    const where = `Chance event '${e.id ?? ''}'`;
    if (!(e.name ?? '').trim()) at('name', `${where} needs a name.`);
    if ((e.weight ?? 10) < 0) at('weight', `${where} weight can't be negative.`);
    // The C# keeps the LAST chain with the id.
    let chain: NonNullable<Bundle['beatChains']>[number] | undefined;
    for (const c of b.beatChains ?? [])
      if (c != null && (c.id ?? '') === (e.chainId ?? '')) chain = c;
    if (!chain) at('chainId', `${where} chain '${e.chainId ?? ''}' doesn't exist.`);
    else if (chainsUsed.has(chain.id ?? ''))
      at('chainId', `${where} shares chain '${chain.id}' with another event.`);
    else {
      chainsUsed.add(chain.id ?? '');
      if (chain.status === 'retired')
        at('chainId', `${where} plays the retired chain '${chain.id}'.`);
    }

    const el = e.eligibility ?? {};
    const squadMin = el.squadMin ?? 1;
    if (squadMin < 1 || squadMin > MAX_SQUAD_SIZE)
      at('eligibility.squadMin', `${where} needs eligibility.squadMin in 1..${MAX_SQUAD_SIZE}.`);
    for (const m of el.modes ?? [])
      if (!(CHANCE_MODES as readonly string[]).includes(m))
        at('eligibility.modes', `${where} mode '${m}' isn't one of ${CHANCE_MODES.join(', ')}.`);
    for (const cat of el.categories ?? [])
      if (
        cat !== '*' &&
        !(b.categories != null && categories.some((x) => x != null && x.id === cat))
      )
        at('eligibility.categories', `${where} category '${cat}' doesn't exist.`);
    if (chain && squadMin < 2)
      for (const role of chain.cast ?? [])
        if (role?.bind === 'squad_member')
          at(
            'eligibility.squadMin',
            `${where} binds squad member role '${role.role ?? ''}', so it needs eligibility.squadMin 2 (solo raids draw only NPC events).`,
          );

    const t = e.trigger ?? {};
    const kind = t.kind ?? 'enter_room';
    if (!TRIGGER_KINDS.includes(kind))
      at(
        'trigger.kind',
        `${where} trigger kind '${kind}' isn't one of ${TRIGGER_KINDS.join(', ')}.`,
      );
    const minRaidS = t.minRaidS ?? 0;
    if (minRaidS < 0 || minRaidS > 600)
      at('trigger.minRaidS', `${where} needs trigger.minRaidS in 0..600.`);
    if (kind === 'enter_room') {
      const area = t.room === null ? undefined : (t.room?.minAreaM2 ?? 0);
      if (area === undefined || area < 0 || area > 400)
        at('trigger.room.minAreaM2', `${where} needs trigger.room.minAreaM2 in 0..400.`);
    }
    if (kind === 'pass_set_piece' && !setPieces.has(t.setPieceId ?? ''))
      at('trigger.setPieceId', `${where} set piece '${t.setPieceId ?? ''}' doesn't exist.`);
  }
}
